// Risolve un driver/manager da una lista per id "legacy": può essere il doc id
// Firestore o l'account_id (uid Auth) collegato allo stesso record. Guardia
// contro `id` falsy: senza, `d.account_id === id` risulterebbe vero per il
// primo elemento della lista con account_id assente ogni volta che `id` è
// undefined (nessun autista/gestore assegnato) — mostrando un driver a caso
// al posto di "nessun autista".
export function findByIdOrAccountId(list, id) {
  if (!id || !Array.isArray(list)) return undefined;
  return list.find((item) => item?.id === id || item?.account_id === id);
}

// Confronta un id "legacy" (id_autista/driver_id/secondo_operatore/...) con un
// driver noto, su doc id o account_id. Guardia su `driver.account_id`: senza,
// `value === driver.account_id` risulterebbe vero ogni volta che ENTRAMBI sono
// undefined (un'attività senza autista assegnato E un driver senza account
// collegato) — facendo apparire quell'attività come sua in tutti i pannelli
// che aggregano per driver (es. stato flotta / prossima attività).
export function matchesDriver(value, driver) {
  if (!value || !driver) return false;
  return value === driver.id || (!!driver.account_id && value === driver.account_id);
}

const timestampMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts.seconds != null) return ts.seconds * 1000;
  if (ts._seconds != null) return ts._seconds * 1000;
  const ms = new Date(ts).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Van id to read a trip's live position from (the `van_id` of the `/vehicles`
 * rows, which `MapContainerMultiStop` matches): the driver's latest vehicle
 * assignment — matched on doc id or account_id, like the order's `id_autista`
 * — else `fallbackVehicleId` (the order's own `vehicle_id`).
 */
export function resolveTripVehicleId({ driver, assignments = [], fallbackVehicleId = null }) {
  let latest = null;
  for (const a of assignments) {
    if (!a || a.deleted || !a.vehicle_id || !matchesDriver(a.driver_id, driver)) continue;
    if (!latest || timestampMs(a.timestamp) > timestampMs(latest.timestamp)) latest = a;
  }
  return latest?.vehicle_id || fallbackVehicleId || null;
}

/** "Nome Cognome" of a driver document. */
export const driverFullName = (driver) =>
  driver ? `${driver.nome || ''} ${driver.cognome || ''}`.replace(/\s+/g, ' ').trim() : '';

/**
 * Who drives a daily trip or an order, by name, read from the driver's id —
 * never from the saved `nome_autista`, which on many daily trips is stale or
 * missing (the old backend writer set `autista` only). On a daily trip the
 * driver is `autista` (what the timeline and the order generator read), on an
 * order `id_autista`; both are tried. A driver not in `drivers` (another
 * manager's) falls back to the saved name, then to nothing.
 */
export function assignedDriverName(record, drivers = []) {
  const id = record?.autista || record?.id_autista;
  if (!id) return '';
  return driverFullName(findByIdOrAccountId(drivers, id)) || record.nome_autista || '';
}
